import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ProviderTable,
  type Provider,
  Button,
  AppSidebar,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  Separator,
  SupportFeedbackDialog,
  AdminAccountDialog,
  AdminProviderInformationDialog,
  type ProviderSchedule,
  type ProviderAvailabilityOverride,
} from '@joinomu/ui'
import { useAuth } from '@/hooks/useAuth'
import { authService, emailService, supabase } from '@joinomu/shared'

export function AdminProvidersPage() {
  const { user, userRole } = useAuth()
  const navigate = useNavigate()
  const [providers, setProviders] = useState<Provider[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [providerSchedules, setProviderSchedules] = useState<ProviderSchedule[]>([])
  const [providerOverrides, setProviderOverrides] = useState<ProviderAvailabilityOverride[]>([])

  // Support/Feedback dialog state
  const [supportFeedbackDialogOpen, setSupportFeedbackDialogOpen] = useState(false)
  const [supportFeedbackType, setSupportFeedbackType] = useState<'support' | 'feedback'>('support')
  const [accountDialogOpen, setAccountDialogOpen] = useState(false)

  useEffect(() => {
    // Redirect if not admin
    if (userRole && userRole !== 'admin') {
      navigate('/')
      return
    }

    if (userRole === 'admin') {
      fetchProviders()
    }
  }, [userRole, navigate])

  const fetchProviders = async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error } = await authService.getAllProviders()

      if (error) {
        setError('Failed to load providers: ' + error.message)
        console.error('Error fetching providers:', error)
        return
      }

      // Fetch additional data for each provider (assigned patients count, upcoming visits)
      const enrichedProviders = await Promise.all(
        (data || []).map(async (provider: any) => {
          // Get assigned patients count
          const { count: patientsCount } = await supabase
            .from('patient_provider_assignments')
            .select('*', { count: 'exact', head: true })
            .eq('provider_id', provider.id)

          // Get upcoming visits count
          const today = new Date().toISOString().split('T')[0]
          const { count: visitsCount } = await supabase
            .from('patient_visits')
            .select('*', { count: 'exact', head: true })
            .eq('provider_id', provider.id)
            .gte('appointment_date', today)
            .eq('status', 'scheduled')

          return {
            ...provider,
            is_active: provider.active,
            assigned_patients_count: patientsCount || 0,
            upcoming_visits_count: visitsCount || 0,
          }
        })
      )

      setProviders(enrichedProviders)
    } catch (err) {
      setError('An unexpected error occurred while loading providers')
      console.error('Exception fetching providers:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchProviderSchedules = async (providerId: string) => {
    try {
      const { data, error } = await supabase
        .from('provider_schedules')
        .select('*')
        .eq('provider_id', providerId)
        .order('day_of_week', { ascending: true })

      if (error) {
        console.error('Error fetching provider schedules:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Exception fetching provider schedules:', error)
      return []
    }
  }

  const fetchProviderOverrides = async (providerId: string) => {
    try {
      const { data, error } = await supabase
        .from('provider_availability_overrides')
        .select('*')
        .eq('provider_id', providerId)
        .gte('date', new Date().toISOString().split('T')[0])
        .order('date', { ascending: true })

      if (error) {
        console.error('Error fetching provider overrides:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Exception fetching provider overrides:', error)
      return []
    }
  }

  const handleProviderClick = async (provider: Provider) => {
    console.log('Provider clicked:', provider)
    setSelectedProvider(provider)

    // Fetch provider schedules and overrides
    const [schedules, overrides] = await Promise.all([
      fetchProviderSchedules(provider.id),
      fetchProviderOverrides(provider.id),
    ])

    setProviderSchedules(schedules)
    setProviderOverrides(overrides)
    setDialogOpen(true)
  }

  const handleSaveSchedule = async (schedules: ProviderSchedule[]): Promise<{ success: boolean; error?: string }> => {
    if (!selectedProvider) return { success: false, error: 'No provider selected' }

    try {
      // Delete existing schedules for this provider
      const { error: deleteError } = await supabase
        .from('provider_schedules')
        .delete()
        .eq('provider_id', selectedProvider.id)

      if (deleteError) {
        console.error('Error deleting existing schedules:', deleteError)
        return { success: false, error: deleteError.message }
      }

      // Insert updated schedules (only ones that are available)
      const schedulesToInsert = schedules
        .filter(s => s.is_available !== false)
        .map(s => ({
          provider_id: selectedProvider.id,
          day_of_week: s.day_of_week,
          start_time: s.start_time,
          end_time: s.end_time,
          is_available: s.is_available !== false,
          treatment_types: s.treatment_types || ['weight_loss', 'diabetes_management'],
        }))

      if (schedulesToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from('provider_schedules')
          .insert(schedulesToInsert)

        if (insertError) {
          console.error('Error inserting schedules:', insertError)
          return { success: false, error: insertError.message }
        }
      }

      // Refresh schedules
      const updatedSchedules = await fetchProviderSchedules(selectedProvider.id)
      setProviderSchedules(updatedSchedules)

      return { success: true }
    } catch (error) {
      console.error('Exception saving schedules:', error)
      return { success: false, error: 'An unexpected error occurred' }
    }
  }

  const handleAddOverride = async (override: Omit<ProviderAvailabilityOverride, 'id'>): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase
        .from('provider_availability_overrides')
        .insert({
          provider_id: override.provider_id,
          date: override.date,
          start_time: override.start_time,
          end_time: override.end_time,
          available: override.available,
          reason: override.reason,
        })

      if (error) {
        console.error('Error adding override:', error)
        return { success: false, error: error.message }
      }

      // Refresh overrides
      const updatedOverrides = await fetchProviderOverrides(override.provider_id)
      setProviderOverrides(updatedOverrides)

      return { success: true }
    } catch (error) {
      console.error('Exception adding override:', error)
      return { success: false, error: 'An unexpected error occurred' }
    }
  }

  const handleDeleteOverride = async (overrideId: string): Promise<{ success: boolean; error?: string }> => {
    if (!selectedProvider) return { success: false, error: 'No provider selected' }

    try {
      const { error } = await supabase
        .from('provider_availability_overrides')
        .delete()
        .eq('id', overrideId)

      if (error) {
        console.error('Error deleting override:', error)
        return { success: false, error: error.message }
      }

      // Refresh overrides
      const updatedOverrides = await fetchProviderOverrides(selectedProvider.id)
      setProviderOverrides(updatedOverrides)

      return { success: true }
    } catch (error) {
      console.error('Exception deleting override:', error)
      return { success: false, error: 'An unexpected error occurred' }
    }
  }

  const handleSignOut = async () => {
    await authService.signOut()
    navigate('/')
  }

  const handleAccountClick = () => {
    setAccountDialogOpen(true)
  }

  const handleSupportFeedbackClick = (type: "Support" | "Feedback") => {
    setSupportFeedbackType(type.toLowerCase() as 'support' | 'feedback')
    setSupportFeedbackDialogOpen(true)
  }

  const handleSupportFeedbackSubmit = async (message: string, type: 'support' | 'feedback') => {
    if (!user?.email) {
      return {
        success: false,
        error: 'User email not available'
      }
    }

    try {
      const result = await emailService.sendSupportFeedback({
        type,
        message,
        userEmail: user.email,
        userName: user.user_metadata?.full_name || user.email.split('@')[0] || 'Anonymous Admin'
      })

      return result
    } catch (error) {
      console.error('Failed to send support/feedback message:', error)
      return {
        success: false,
        error: 'Failed to send message'
      }
    }
  }

  if (userRole && userRole !== 'admin') {
    return null // Will redirect
  }

  const userData = {
    name: user?.email?.split('@')[0] || 'Admin',
    email: user?.email || 'admin@example.com',
    avatar: ''
  }

  return (
    <SidebarProvider>
      <AppSidebar
        user={userData}
        onLogout={handleSignOut}
        userRole="admin"
        onNavigate={(item: string) => {
          if (item === 'Dashboard') navigate('/admin/dashboard')
          else if (item === 'Patients') navigate('/admin/patients')
          else if (item === 'Providers') navigate('/admin/providers')
          else if (item === 'Messaging') navigate('/admin/messaging')
        }}
        onAccountClick={handleAccountClick}
        onSupportFeedbackClick={handleSupportFeedbackClick}
      />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>Provider Management</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>
        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
          {error && (
            <div className="border border-destructive/20 bg-destructive/10 rounded-lg p-4 mx-6 mt-6">
              <p className="text-destructive">{error}</p>
              <Button onClick={fetchProviders} className="mt-4">
                Try Again
              </Button>
            </div>
          )}

          <ProviderTable
            title="Provider Management"
            subtitle={loading ? "Loading providers..." : `Manage all providers in the system. Total: ${providers.length} providers`}
            providers={providers}
            loading={loading}
            showSearch={true}
            onProviderClick={handleProviderClick}
            searchPlaceholder="Search providers by name, email, or specialty..."
            emptyStateText="No providers found."
          />
        </div>
      </SidebarInset>

      {/* Provider Information Dialog */}
      <AdminProviderInformationDialog
        provider={selectedProvider ? {
          id: selectedProvider.id,
          profile_id: selectedProvider.profile_id,
          first_name: selectedProvider.first_name,
          last_name: selectedProvider.last_name,
          email: selectedProvider.email,
          phone: selectedProvider.phone,
          specialty: selectedProvider.specialty,
          license_number: selectedProvider.license_number,
          is_active: selectedProvider.is_active,
          created_at: selectedProvider.created_at,
          treatment_types: selectedProvider.treatment_types,
        } : null}
        schedules={providerSchedules}
        overrides={providerOverrides}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaveSchedule={handleSaveSchedule}
        onAddOverride={handleAddOverride}
        onDeleteOverride={handleDeleteOverride}
        initialSection="Info"
      />

      <AdminAccountDialog
        open={accountDialogOpen}
        onOpenChange={setAccountDialogOpen}
      />

      <SupportFeedbackDialog
        open={supportFeedbackDialogOpen}
        onOpenChange={setSupportFeedbackDialogOpen}
        type={supportFeedbackType}
        onSubmit={handleSupportFeedbackSubmit}
      />
    </SidebarProvider>
  )
}
